import React, { Component } from 'react'
import Helmet from 'react-helmet'
import { browserHistory, RouteComponentProps } from 'react-router'
import { AppMainView, AppView } from '../../../../../components/AppView'
import AppHeader from '../../../../../components/AppView/Header'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { HelpLinkMenu } from '../../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { ResponsiveMainView } from '../../../../../components/ResponsiveMainView'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { PermissionNames } from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { Row, Col, message, Skeleton, Alert } from 'antd'
import { AddDiscountGeneral } from './AddDiscountGeneral'
import { CollapseProps } from 'antd/lib/collapse'
import './AddDiscount.scss'
import { Routes } from '../../../../../constants/Routes'
import { AddDiscountType } from './AddDiscountType'
import { AddDiscountProducts } from './AddDiscountProducts'
import { DiscountStore } from './DiscountStore'
import { AddDiscountTime } from './AddDiscountTime'
import { AddDiscountLoyalty } from './AddDiscountLoyalty'
import { Flip } from 'react-reveal'
import DepreciatedDiscountImage from '../../../../../images/img-discount-depreciated.png'
import { discountApi, DiscountModels } from '@getgreenline/products'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { NoPermissionsResult } from '../../../../../components/NoPermissionsResult'
import { TApplyDiscount } from '../Discount.interface'
import { BLAZE_COMPANY } from '../../../../../constants/general'
import { HelpLinks } from '../../../../../constants/ExternalLinks'

interface Props extends RouteComponentProps<GlobalParam, RouteParam> {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface State {
  isLoading: boolean
  isSaving: boolean
  isDiscountUndefined: boolean
  isDiscountCodeValid: boolean
  currentTabType: DiscountModels.ApplyType
  currentApplyTypeOption: TApplyDiscount
  enableLoyaltyTargeting: boolean
}

interface GlobalParam {
  companyId: string
}

interface RouteParam {
  discountId?: string
}

function getDiscountCurrentTabType(
  applyType: DiscountModels.ApplyType,
  applyTypeOption: TApplyDiscount,
) {
  return applyType === 'cart' && applyTypeOption === 'product' ? 'bundle' : applyType
}

function getAppliedDiscountType(
  discount: DiscountModels.CreateOrEditDiscount | DiscountModels.IDiscount,
): TApplyDiscount {
  if (!discount.id) return undefined
  const hasProducts = discount.products.length > 0
  const hasCategories = discount.categories.length > 0
  let hasOnlyExcludedCategories = true
  let hasOnlyExcludedProducts = true
  let hasSomeIncludedProducts = false
  let hasSomeIncludedCategories = false

  for (const category of discount.categories) {
    if (category.type !== 'exclude') hasOnlyExcludedCategories = false // achieves same as discount.categories.every(e => e.type === 'exclude')
    if (category.type === 'include') hasSomeIncludedCategories = true // achieves same as discount.categories.some(e => e.type === 'include')
  }

  for (const product of discount.products) {
    if (product.type !== 'exclude') hasOnlyExcludedProducts = false // achieves same as discount.products.every(e => e.type === 'exclude')
    if (product.type === 'include') hasSomeIncludedProducts = true // achieves same as discount.products.some(e => e.type === 'include')
  }

  if (
    (!hasProducts && !hasCategories) ||
    (!hasProducts && hasOnlyExcludedCategories) ||
    (!hasCategories && hasOnlyExcludedProducts) ||
    (hasOnlyExcludedProducts && hasOnlyExcludedCategories)
  )
    return 'all'
  if (hasSomeIncludedProducts && hasSomeIncludedCategories) return undefined
  if (hasSomeIncludedCategories) return 'category'
  if (hasProducts && !hasCategories) return 'product'
  return undefined
}

function getDefaultSelectedApplyTypeProductForApplyTypeTab(
  currentSelectedApplyTypeTab: DiscountModels.ApplyType,
): TApplyDiscount {
  if (['products', 'bundle'].includes(currentSelectedApplyTypeTab)) return 'product'
  if (currentSelectedApplyTypeTab == 'cart') return 'category'
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class AddDiscount extends Component<Props, State> {
  company = this.props.currentCompanyStore!.company!
  productStore = this.props.currentCompanyStore!.productStore!

  discount = new DiscountModels.CreateOrEditDiscount()

  discountStore = new DiscountStore(this.discount, this.productStore)

  defaultFlipAnimationProps: ReactRevealProps = { top: true, appear: true, duration: 400 }
  defaultFadeAnimationProps: ReactRevealProps = {
    top: true,
    appear: true,
    duration: 400,
    distance: '4px',
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: true,
      isSaving: false,
      isDiscountUndefined: false,
      isDiscountCodeValid: true,
      currentTabType: 'products',
      currentApplyTypeOption: 'product',
      enableLoyaltyTargeting: !!this.discount.loyaltyTierId,
    }
  }

  componentDidMount = async () => {
    try {
      this.setState({ isLoading: true })

      this.props.currentCompanyStore!.getCanUseLoyaltyService()
      this.props.currentCompanyStore!.getCanUseDiscountMultipleLoyaltyTiers()

      if (!this.props.currentCompanyStore!.externalLoyaltyProvider)
        this.props.currentCompanyStore!.getExternalLoyaltyProvider(this.company.id)

      const loyaltyTiers = await this.props.currentCompanyStore!.getLoyaltyTiers()
      const locations = await this.props.currentCompanyStore!.getLocations()
      const categories = await this.productStore.getCategories()

      await Promise.all([loyaltyTiers, locations, categories])

      this.discountStore.setFlattenedMappedCategory()

      const discountId = this.props.params.discountId

      if (discountId) {
        const discount = await discountApi.getDiscountByIdV2(this.company.id, parseInt(discountId))
        this.discount.set(discount)
        this.setState({ isDiscountCodeValid: this.validateDiscountCode(discount.discountCode) })
        this.discountStore.setMappedSelectedCategory()
        this.rerenderDiscountTypeAndOptions(discount)
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading discount')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  rerenderDiscountTypeAndOptions(
    discount: DiscountModels.CreateOrEditDiscount | DiscountModels.IDiscount,
  ) {
    const appliedDiscountType = getAppliedDiscountType(discount)
    const currentTabType = getDiscountCurrentTabType(discount.applyType, appliedDiscountType)

    this.setState({
      currentTabType,
      currentApplyTypeOption: appliedDiscountType,
    })
  }

  onSave = async () => {
    try {
      this.setState({ isSaving: true })

      if (!this.discount.isValid) {
        this.discount.sanitize()
      }

      const trimmedName = this.discount.name.trim()
      this.discount.setName(trimmedName)

      if (this.shouldDisableAutoLoyaltyDiscount) {
        this.discount.setLoyaltyTierId(null)
        this.discount.setLoyaltyTiers([])
      }

      const skipAutoDiscountValidation =
        this.discount.automatic && this.props.currentCompanyStore?.canUseOverlappingAutoDiscounts
          ? true
          : undefined

      const newDiscount = this.discount.id
        ? await this.productStore.editDiscount(this.discount)
        : await this.productStore.addDiscount(this.discount, skipAutoDiscountValidation)

      await browserHistory.replace(
        Routes.DASHBOARD_PRODUCTS_EDIT_DISCOUNT(this.company.id, newDiscount.id),
      )

      this.discount.set(newDiscount)

      await message.success('Successfully saved discount')
    } catch (error) {
      message.error(parseErrorMsg(error || 'An error occurred while saving discount'))
    } finally {
      this.setState({ isSaving: false })
    }
  }

  get responsiveRatio() {
    return { '2/3': { xs: 24, md: 16 }, '1/3': { xs: 24, md: 8 } }
  }

  get isDiscountAllowedToSave() {
    const isAppliedToAllDiscountsAndCategories = this.state.currentApplyTypeOption === 'all'
    const hasProductOrCategoryItems =
      this.discount.products.length + this.discount.categories.length > 0
    return hasProductOrCategoryItems || isAppliedToAllDiscountsAndCategories
  }

  updateDiscountLoyaltyCost = (value?: string | number): void => {
    const numberValue = value ? parseFloat(value.toString().replace(/[^0-9.]/g, '')) : undefined
    this.discount.setLoyaltyCost(numberValue || 0)

    if (numberValue) {
      this.discount.setAutomaticDiscount(false)
    }
  }

  get isEditing() {
    return Boolean(this.props.routeParams.discountId)
  }

  get shouldDisableAutoLoyaltyDiscount() {
    return this.discount.automatic && !this.props.currentCompanyStore?.canUseAutoLoyaltyDiscount
  }

  setEnableLoyaltyTargeting = (value: boolean) => {
    this.setState({ enableLoyaltyTargeting: value })
  }

  validateDiscountCode = (value?: string | null) => {
    return value === null || value === undefined || /^[A-Z0-9]*$/.test(value)
  }

  handleDiscountCodeChange = (value: string) => {
    this.discount.discountCode = value || null
    this.setState({ isDiscountCodeValid: this.validateDiscountCode(value) })
  }

  get shouldShowEcomSalesChannelWarning() {
    const { salesChannels, discountCode, automatic } = this.discount

    const includesEcom =
      salesChannels.length === 0 ||
      salesChannels.some(({ id }) => id === DiscountModels.SalesChannels.ECOM.id)

    return includesEcom && !discountCode && !automatic
  }

  render() {
    const { currentTabType, currentApplyTypeOption } = this.state

    if (
      !this.props.currentUserStore?.hasPermissionAtEntity(
        PermissionNames.CAN_EDIT_DISCOUNTS,
        this.company.id,
      )
    ) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_EDIT_DISCOUNTS} />
    }

    const pageTitle = this.isEditing ? 'Edit discount' : 'Add discount'
    const sharedCollapseProps: CollapseProps = {
      style: { width: '100%', marginBottom: 16 },
      expandIconPosition: 'right',
      expandIcon: (panelProps) => {
        const expandText = panelProps.isActive ? 'Collapse' : 'Expand'
        return <p style={{ color: 'var(--blue)' }}>{expandText}</p>
      },
    }
    const canAccessLoyalty = this.company.permissions.canAccessLoyalty

    return (
      <AppView column>
        <Helmet>
          <title>Products | Discounts | {pageTitle}</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader
          title={pageTitle}
          backVisible
          onBackClick={() =>
            browserHistory.push(Routes.DASHBOARD_PRODUCTS_DISCOUNTS(this.company.id))
          }
        >
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={[
                {
                  href: HelpLinks.PRODUCTS_DISCOUNTS,
                  title: 'Adding discounts',
                },
              ]}
            />
            <BlazeButton
              buttonProps={{
                disabled: !(this.isDiscountAllowedToSave && this.state.isDiscountCodeValid),
                type: 'primary',
                style: { width: 200 },
                loading: this.state.isSaving,
                onClick: this.onSave,
              }}
              titleProps={{
                title: this.state.isSaving ? 'Saving' : 'Save',
              }}
            />
          </span>
        </AppHeader>
        <AppMainView className='background-grey intercom-pb-2'>
          {this.state.isLoading ? (
            <div className='white-rounded-container p-4'>
              <Skeleton active />
              <Skeleton active />
              <Skeleton active />
              <Skeleton active />
            </div>
          ) : (
            <ResponsiveMainView>
              <Row type='flex' gutter={[16, 16]}>
                <Col {...this.responsiveRatio['2/3']} className='order-1 order-md-0'>
                  <AddDiscountGeneral
                    discount={this.discount}
                    collapseProps={sharedCollapseProps}
                    locations={this.props.currentCompanyStore!.locations || []}
                  />
                  <AddDiscountType
                    discount={this.discount}
                    collapseProps={sharedCollapseProps}
                    isDiscountUndefined={this.state.isDiscountUndefined}
                    isDiscountCodeValid={this.state.isDiscountCodeValid}
                    currentTabType={currentTabType}
                    setCurrentTabType={(currentTabType) => {
                      // this will run each time a tab switch occurs
                      this.discount.setApplyType(
                        currentTabType === 'bundle' ? 'cart' : currentTabType,
                      )
                      const hasProducts = this.discount.products.length > 0
                      const hasCategories = this.discount.categories.length > 0
                      const defaultAppliedDiscountType =
                        getDefaultSelectedApplyTypeProductForApplyTypeTab(currentTabType)
                      const currentApplyTypeOption =
                        this.discount.id && (hasProducts || hasCategories)
                          ? getAppliedDiscountType(this.discount)
                          : defaultAppliedDiscountType
                      this.setState({ currentTabType, currentApplyTypeOption })
                    }}
                    setEnableLoyaltyTargeting={this.setEnableLoyaltyTargeting}
                    onDiscountCodeChange={this.handleDiscountCodeChange}
                  />
                  <AddDiscountProducts
                    discount={this.discount}
                    collapseProps={sharedCollapseProps}
                    discountStore={this.discountStore}
                    defaultFadeAnimationProps={this.defaultFadeAnimationProps}
                    currentTabType={currentTabType}
                    currentApplyTypeOption={currentApplyTypeOption}
                    setCurrentApplyTypeOption={(currentApplyTypeOption: TApplyDiscount) => {
                      this.setState({ currentApplyTypeOption })
                    }}
                    setDiscountTypeUndefined={(isDiscountUndefined: boolean) => {
                      this.setState({ isDiscountUndefined })
                    }}
                  />
                  <AddDiscountTime
                    discount={this.discount}
                    collapseProps={sharedCollapseProps}
                    defaultFadeAnimationProps={this.defaultFadeAnimationProps}
                  />
                  {canAccessLoyalty && (
                    <AddDiscountLoyalty
                      discount={this.discount}
                      collapseProps={sharedCollapseProps}
                      loyaltyTiers={this.props.currentCompanyStore!.loyaltyTiers || []}
                      defaultFadeAnimationProps={this.defaultFadeAnimationProps}
                      updateDiscountLoyaltyCost={this.updateDiscountLoyaltyCost}
                      enableLoyaltyTargeting={this.state.enableLoyaltyTargeting}
                      setEnableLoyaltyTargeting={this.setEnableLoyaltyTargeting}
                    />
                  )}
                </Col>
                <Col {...this.responsiveRatio['1/3']} className='order-0 order-md-1'>
                  <div className='discount-alerts'>
                    <Alert
                      type='info'
                      showIcon={true}
                      message='All discounts are applied before taxes'
                    />
                    {this.discount.applyType === 'cart' && (
                      <Flip {...this.defaultFlipAnimationProps}>
                        <Alert
                          type='warning'
                          showIcon
                          message='Only product discounts can be automatic'
                        />
                      </Flip>
                    )}
                    {!this.isDiscountAllowedToSave && (
                      <Flip {...this.defaultFlipAnimationProps}>
                        <Alert
                          type='warning'
                          showIcon
                          message='A discount should apply to at least one product or category before it can be saved!'
                        />
                      </Flip>
                    )}
                    {canAccessLoyalty && this.shouldDisableAutoLoyaltyDiscount && (
                      <Flip {...this.defaultFlipAnimationProps}>
                        <Alert
                          type='warning'
                          showIcon
                          message={'Loyalty is disabled. To enable, set automatic discount off'}
                        />
                      </Flip>
                    )}
                    {this.state.isDiscountUndefined && (
                      <Flip {...this.defaultFlipAnimationProps}>
                        <Alert
                          type='error'
                          showIcon
                          message={
                            <div>
                              {BLAZE_COMPANY} no longer supports the discount type with{' '}
                              <strong>included products</strong> and{' '}
                              <strong>included categories</strong> at one discount.
                              <br />
                              <br />
                              Please set discounts for products and categories separately
                              <br />
                              <br />
                              <a href={DepreciatedDiscountImage} target='_blank' rel='noreferrer'>
                                <img src={DepreciatedDiscountImage} className='w-100' />
                              </a>
                            </div>
                          }
                        />
                      </Flip>
                    )}
                    {this.state.isDiscountCodeValid === false && (
                      <Flip {...this.defaultFlipAnimationProps}>
                        <Alert
                          type='error'
                          showIcon
                          message={
                            'Current discount code is invalid and should be fixed to enable saving. Discount codes should be alphanumeric, uppercase and without blank spaces'
                          }
                        />
                      </Flip>
                    )}
                    {this.shouldShowEcomSalesChannelWarning && (
                      <Flip {...this.defaultFlipAnimationProps}>
                        <Alert
                          type='warning'
                          showIcon
                          message={
                            'A discount needs to be automatic or have a discount code to work with ECOM'
                          }
                        />
                      </Flip>
                    )}
                  </div>
                </Col>
              </Row>
            </ResponsiveMainView>
          )}
        </AppMainView>
      </AppView>
    )
  }
}
